/**
 * Job API functions
 * Handles fetching job diversion stats and material summaries
 */

import type {
  JobDiversionStats,
  JobSummaryResponse,
} from '../types/job';
import { ensureJobDiversionStats, ensureJobSummaryResponse } from '@/lib/api/type-guards';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Fetch diversion statistics for a job
 */
export async function fetchJobDiversionStats(
  jobId: string
): Promise<JobDiversionStats> {
  const response = await fetch(`${API_BASE_URL}/api/v1/jobs/${jobId}/diversion`);

  if (!response.ok) {
    throw new Error(`Failed to fetch job diversion stats: ${response.statusText}`);
  }

  const data = await response.json();

  // API returns { diversion: {...} }, extract and map the fields
  const diversion = data.diversion || {};
  const mappedData = {
    total_weight: diversion.total_weight_tons || 0,
    diverted_weight: diversion.diverted_weight_tons || 0,
    landfill_weight: diversion.landfill_weight_tons || 0,
    diversion_rate: diversion.diversion_rate || 0,
    target_diversion_rate: diversion.target_rate || 0,
  };

  // Validate and ensure safe structure
  return ensureJobDiversionStats(mappedData);
}

/**
 * Fetch material breakdown summary for a job
 */
export async function fetchJobMaterialSummary(
  jobId: string
): Promise<JobSummaryResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/jobs/${jobId}/summary`);

  if (!response.ok) {
    throw new Error(`Failed to fetch job material summary: ${response.statusText}`);
  }

  const data = await response.json();

  // API returns { summary: [...] }, map the fields for each material
  interface ApiMaterialSummary {
    material_type: string;
    total_weight_tons: number;
    diverted_weight_tons: number;
    landfill_weight_tons: number;
    diversion_rate: number;
  }

  const summary = (data.summary || []).map((material: ApiMaterialSummary) => ({
    material_type: material.material_type,
    total_weight: material.total_weight_tons || 0,
    diverted_weight: material.diverted_weight_tons || 0,
    landfill_weight: material.landfill_weight_tons || 0,
    diversion_rate: material.diversion_rate || 0,
  }));

  // Validate and ensure safe structure
  return ensureJobSummaryResponse({ summary });
}
